import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet";
import ReCAPTCHA from "react-google-recaptcha";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import Hero from "../../components/Hero/Hero";
import PageFooter from "../../components/PageFooter/PageFooter";
import Modal from "../../components/ui/Modal";
import FormMoreInfo from "../../components/FormMoreInfo/FormMoreInfo";
import toast from "../../components/ui/toast";
import "./reviewSemanticAnalysis.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/tech";

function safeText(val) {
  if (val == null) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number") return String(val);
  if (typeof val === "object") {
    const parts = [
      ...Object.keys(val).filter((k) => typeof k === "string" && k.length > 5),
      ...Object.values(val).filter((v) => typeof v === "string"),
    ];
    if (parts.length > 0) return parts.join(" ");
    return JSON.stringify(val);
  }
  return String(val);
}

const MAX_REVIEWS = 500;
const MAX_THEMES = 5;

const CSV_TEMPLATE =
  'review_content,rating,review_date\n"Super accueil, personnel très agréable.",5,2026-03-01\n"Attente trop longue au comptoir.",2,2026-02-15\n"Bon rapport qualité/prix.",4,\n';

const AVAILABLE_THEMES = [
  { id: "accueil", name: "Accueil", icon: "👋" },
  { id: "service_client", name: "Service client", icon: "🤝" },
  { id: "expertise", name: "Expertise / Compétence", icon: "🎯" },
  { id: "reactivite", name: "Réactivité", icon: "⚡" },
  { id: "qualite_produits", name: "Qualité des produits", icon: "✨" },
  { id: "variete_choix", name: "Variété / Choix", icon: "🛒" },
  { id: "rapport_qualite_prix", name: "Rapport qualité-prix", icon: "💰" },
  { id: "proprete", name: "Propreté / Hygiène", icon: "🧹" },
  { id: "ambiance", name: "Ambiance / Cadre", icon: "🏪" },
  { id: "accessibilite", name: "Accessibilité / Parking", icon: "🅿️" },
  { id: "confort", name: "Confort", icon: "🛋️" },
  { id: "temps_attente", name: "Temps d'attente", icon: "⏱️" },
  { id: "horaires", name: "Horaires", icon: "🕐" },
  { id: "livraison", name: "Livraison", icon: "📦" },
  { id: "disponibilite", name: "Disponibilité", icon: "📋" },
  { id: "communication", name: "Communication", icon: "💬" },
  { id: "sav", name: "SAV / Après-vente", icon: "🔧" },
  { id: "fidelisation", name: "Fidélisation", icon: "❤️" },
  { id: "recommandation", name: "Recommandation", icon: "👍" },
  { id: "reservation", name: "Réservation en ligne", icon: "📱" },
  { id: "transparence", name: "Transparence", icon: "🔍" },
  { id: "fiabilite", name: "Fiabilité", icon: "🛡️" },
  { id: "securite", name: "Sécurité", icon: "🔒" },
  { id: "site_web", name: "Site web / App", icon: "💻" },
];

const SENTIMENT_COLORS = {
  positive: "#10b981",
  negative: "#ef4444",
  neutral: "#6b7280",
  mixed: "#f59e0b",
};

const SENTIMENT_LABELS = {
  positive: "Positif",
  negative: "Négatif",
  neutral: "Neutre",
  mixed: "Mitigé",
};

const ScoreBar = ({ score }) => {
  if (score === null || score === undefined) return null;
  const color = score >= 70 ? "#10b981" : score >= 40 ? "#f59e0b" : "#ef4444";
  return (
    <div className="rsa-score-bar">
      <div
        className="rsa-score-bar__fill"
        style={{ width: `${score}%`, background: color }}
      />
    </div>
  );
};

const SentimentDot = ({ sentiment }) => {
  const color = SENTIMENT_COLORS[sentiment] || "#6b7280";
  return (
    <span
      className="rsa-sentiment-dot"
      style={{ background: color }}
      title={SENTIMENT_LABELS[sentiment] || sentiment}
    />
  );
};

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return [];
  const header = lines[0].toLowerCase().replace(/\s+/g, "");
  if (!header.includes("review_content") && !header.includes("reviewcontent"))
    return [];
  const results = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCSVLine(lines[i]);
    const content = (cols[0] || "").trim();
    if (!content) continue;
    const rating = cols[1] ? parseFloat(cols[1]) : null;
    const date = cols[2] ? cols[2].trim() : null;
    results.push({
      review_content: content,
      rating: rating && rating >= 1 && rating <= 5 ? rating : null,
      review_date: date || null,
    });
  }
  return results;
}

function parseCSVLine(line) {
  const cols = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === "," || ch === ";") {
      cols.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  cols.push(current);
  return cols;
}

const ReviewSemanticAnalysis = () => {
  const [businessName, setBusinessName] = useState("");
  const [businessAddress, setBusinessAddress] = useState("");
  const [reviewsText, setReviewsText] = useState("");
  const [reviews, setReviews] = useState([]);
  const [selectedThemes, setSelectedThemes] = useState([
    "accueil",
    "service_client",
    "qualite_produits",
    "rapport_qualite_prix",
    "temps_attente",
  ]);
  const [inputMode, setInputMode] = useState("text");
  const [csvFileName, setCsvFileName] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [isSA, setIsSA] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const formRef = useRef(null);
  const fileInputRef = useRef(null);
  const recaptchaRef = useRef();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("access") === "localranker") setIsSA(true);
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const parseTextReviews = (text) =>
    text
      .split(/\n{2,}/)
      .map((r) => r.trim())
      .filter((r) => r.length > 0)
      .map((r) => ({ review_content: r, rating: null, review_date: null }));

  useEffect(() => {
    if (inputMode === "text") setReviews(parseTextReviews(reviewsText));
  }, [reviewsText, inputMode]);

  const toggleTheme = (id) => {
    setSelectedThemes((prev) => {
      if (prev.includes(id)) return prev.filter((t) => t !== id);
      if (prev.length >= MAX_THEMES) {
        toast.warning(`Maximum ${MAX_THEMES} thématiques.`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleCSVFile = (file) => {
    if (!file) return;
    if (!file.name.match(/\.(csv|tsv|txt)$/i)) {
      toast.warning("Format non supporté. Utilisez un fichier .csv");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSV(e.target.result);
      if (parsed.length === 0) {
        toast.warning(
          'Aucun avis trouvé. Vérifiez que la première colonne est "review_content".',
        );
        return;
      }
      setReviews(parsed);
      setCsvFileName(file.name);
      setInputMode("csv");
    };
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleCSVFile(e.dataTransfer?.files?.[0]);
  };

  const clearCSV = () => {
    setCsvFileName(null);
    setInputMode("text");
    setReviews(parseTextReviews(reviewsText));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv;charset=utf-8;" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u;
    a.download = "reviews_template.csv";
    a.click();
    URL.revokeObjectURL(u);
  };

  const handlePlaceSelect = ({ description }) => {
    if (!description) return;
    const parts = description.split(",");
    setBusinessName(parts[0]?.trim() || description);
    setBusinessAddress(description);
  };

  const handleSubmit = () => {
    if (reviews.length === 0) {
      toast.warning("Ajoutez au moins un avis à analyser.");
      return;
    }
    if (selectedThemes.length === 0) {
      toast.warning("Sélectionnez au moins une thématique.");
      return;
    }

    if (isSA) {
      doSubmit();
    } else {
      setModalVisible(true);
    }
  };

  const doSubmit = async (leadInfo) => {
    setModalVisible(false);

    const tokenCaptcha = isSA
      ? true
      : await recaptchaRef.current?.executeAsync();
    if (!tokenCaptcha) {
      toast.warning("Veuillez résoudre le captcha.");
      return;
    }

    setIsLoading(true);
    setResult(null);

    try {
      const params = new URLSearchParams();
      if (isSA) params.append("sa", "true");
      const qs = params.toString();

      const themes = selectedThemes.map((id) => {
        const t = AVAILABLE_THEMES.find((th) => th.id === id);
        return { id, name: t?.name || id };
      });

      const payload = reviews.slice(0, MAX_REVIEWS).map((r) => ({
        review_content: r.review_content,
        ...(r.rating && { rating: r.rating }),
        ...(r.review_date && { review_date: r.review_date }),
      }));

      const response = await fetch(
        `${API_URL}/reviews-semantic-analysis${qs ? `?${qs}` : ""}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            reviews: payload,
            themes,
            businessName: businessName || undefined,
            businessAddress: businessAddress || undefined,
            ...(leadInfo && {
              businessInfo: {
                ...leadInfo,
                businessName: businessName || undefined,
              },
            }),
          }),
        },
      );

      const data = await response.json();
      if (!response.ok) {
        toast.error(data.error || "Une erreur est survenue.");
        return;
      }
      setResult({
        ...data,
        businessName: businessName || null,
        businessAddress: businessAddress || null,
      });
    } catch (e) {
      console.error(e);
      toast.error("Impossible de contacter le serveur. Réessayez.");
    } finally {
      setIsLoading(false);
      recaptchaRef.current?.reset();
    }
  };

  const reviewCount = reviews.length;

  return (
    <div className="rsa-page">
      <Helmet>
        <title>
          Analyse sémantique des avis clients | Outil gratuit | Localranker
        </title>
        <meta
          name="description"
          content="Analysez gratuitement le sentiment et les thématiques clés de vos avis clients Google. Identifiez vos forces et axes d'amélioration grâce à l'IA."
        />
        <link rel="canonical" href="https://localranker.tech/reviews-semantic-analysis" />
        <meta property="og:title" content="Analyse sémantique des avis clients | Outil gratuit | Localranker" />
        <meta property="og:description" content="Analysez gratuitement le sentiment et les thématiques clés de vos avis clients Google. Identifiez vos forces et axes d'amélioration grâce à l'IA." />
        <meta property="og:url" content="https://localranker.tech/reviews-semantic-analysis" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Localranker" />
      </Helmet>

      <Hero
        title="Analyse sémantique des avis clients"
        subtitle="Importez vos avis, choisissez vos thématiques et obtenez un rapport d'analyse avec score par thème, SWOT et recommandations."
        ctaText="Analyser mes avis"
        ctaOnClick={scrollToForm}
      />

      <div className="rsa-form" ref={formRef}>
        <div className="rsa-form__card">
          {/* Step 1: Business */}
          <div className="rsa-form__step">
            <div className="rsa-form__step-label">
              <span className="rsa-form__step-num">1</span>
              Votre établissement
            </div>
            <div className="rsa-form__place-input">
              <GooglePlacesAutocomplete
                placeholder="Recherchez votre établissement..."
                onSelect={handlePlaceSelect}
                renderSuggestions={(active, suggestions, onSelectSuggestion) => (
                  <div className="rsa-suggestions">
                    {suggestions.map((suggestion, index) => (
                      <div
                        className="rsa-suggestion"
                        key={index}
                        onClick={(event) =>
                          onSelectSuggestion(suggestion, event)
                        }
                      >
                        <svg
                          className="rsa-suggestion__icon"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                        {suggestion.description}
                      </div>
                    ))}
                  </div>
                )}
              />
            </div>
            {businessAddress && (
              <p className="rsa-form__place-selected">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                {businessAddress}
              </p>
            )}
          </div>

          <div className="rsa-form__divider" />

          {/* Step 2: Input */}
          <div className="rsa-form__step">
            <div className="rsa-form__step-label">
              <span className="rsa-form__step-num">2</span>
              Importez vos avis
            </div>
            <div className="rsa-form__tabs">
              <button
                type="button"
                className={`rsa-form__tab${inputMode === "text" ? " rsa-form__tab--active" : ""}`}
                onClick={clearCSV}
              >
                Coller des avis
              </button>
              <button
                type="button"
                className={`rsa-form__tab${inputMode === "csv" ? " rsa-form__tab--active" : ""}`}
                onClick={() => fileInputRef.current?.click()}
              >
                Importer un CSV
              </button>
            </div>

            {inputMode === "text" && (
              <>
                <div
                  className={`rsa-form__drop-zone${isDragging ? " rsa-form__drop-zone--active" : ""}`}
                  onDrop={handleDrop}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                >
                  <textarea
                    className="rsa-form__textarea"
                    placeholder="Collez vos avis ici, un par paragraphe (séparez chaque avis par une ligne vide)..."
                    value={reviewsText}
                    onChange={(e) => setReviewsText(e.target.value)}
                    rows={5}
                  />
                  {isDragging && (
                    <div className="rsa-form__drop-overlay">
                      <span>Déposez votre fichier CSV ici</span>
                    </div>
                  )}
                </div>
                <p className="rsa-form__hint">
                  Ou glissez un fichier CSV.{" "}
                  <button
                    type="button"
                    className="rsa-form__link"
                    onClick={downloadTemplate}
                  >
                    Télécharger le modèle CSV
                  </button>
                </p>
              </>
            )}

            {inputMode === "csv" && (
              <div className="rsa-form__csv-file">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                <span className="rsa-form__csv-name">{csvFileName}</span>
                <span className="rsa-form__csv-count">
                  {reviewCount} avis
                </span>
                <button
                  type="button"
                  className="rsa-form__csv-remove"
                  onClick={clearCSV}
                >
                  &times;
                </button>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.tsv,.txt"
              style={{ display: "none" }}
              onChange={(e) => handleCSVFile(e.target.files?.[0])}
            />
          </div>

          <div className="rsa-form__divider" />

          {/* Step 3: Theme picker */}
          <div className="rsa-form__step">
            <div className="rsa-form__step-label">
              <span className="rsa-form__step-num">3</span>
              Thématiques ({selectedThemes.length}/{MAX_THEMES})
            </div>
            <div className="rsa-form__theme-grid">
              {AVAILABLE_THEMES.map((t) => {
                const active = selectedThemes.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={`rsa-form__theme-chip${active ? " rsa-form__theme-chip--active" : ""}`}
                    onClick={() => toggleTheme(t.id)}
                  >
                    <span className="rsa-form__theme-chip-icon">{t.icon}</span>
                    {t.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rsa-form__divider" />

          {/* Footer */}
          <div className="rsa-form__footer">
            <div className="rsa-form__footer-info">
              <span className="rsa-form__count">
                {reviewCount} avis
                {reviewCount > MAX_REVIEWS && (
                  <span className="rsa-form__count--warn">
                    {" "}(max {MAX_REVIEWS})
                  </span>
                )}
              </span>
            </div>
            <button
              className="rsa-form__btn"
              type="button"
              onClick={handleSubmit}
              disabled={
                isLoading || reviewCount === 0 || selectedThemes.length === 0
              }
            >
              {isLoading ? "Analyse en cours..." : "Lancer l'analyse"}
            </button>
          </div>
        </div>
      </div>

      <ReCAPTCHA
        sitekey="6LcLC9UZAAAAAOY_vgaU__i3Qhs1tmHtvbibjD2V"
        size="invisible"
        ref={recaptchaRef}
      />

      {isLoading && (
        <div className="rsa-loader">
          <div className="rsa-loader__bar" />
          <p className="rsa-loader__text">
            Analyse de {reviewCount} avis en cours...
          </p>
        </div>
      )}

      {result && (
        <div className="rsa-results">
          <div className="rsa-results__header">
            <h2>
              {result.businessName
                ? `Rapport — ${result.businessName}`
                : "Rapport d'analyse"}
            </h2>
            <p className="rsa-results__meta">
              {result.businessAddress && (
                <span className="rsa-results__address">
                  {result.businessAddress}
                </span>
              )}
              {result.totalReviews} avis analysés
              {result.avgRating !== null && (
                <span>
                  {" "}&middot; Note moyenne{" "}
                  <strong>{result.avgRating}/5</strong>
                </span>
              )}
            </p>
          </div>

          {/* Global score */}
          <div className="rsa-global">
            <div className="rsa-global__score-block">
              <div
                className="rsa-global__score-ring"
                data-sentiment={result.globalSentiment}
              >
                <span className="rsa-global__score-num">
                  {result.globalScore ?? "—"}
                </span>
                <span className="rsa-global__score-label">/100</span>
              </div>
              <span
                className="rsa-global__sentiment-tag"
                style={{
                  color:
                    SENTIMENT_COLORS[result.globalSentiment] || "#6b7280",
                }}
              >
                {SENTIMENT_LABELS[result.globalSentiment] || "—"}
              </span>
            </div>
            {safeText(result.executiveSummary) && (
              <div className="rsa-global__summary">
                <p>{safeText(result.executiveSummary)}</p>
              </div>
            )}
          </div>

          {/* Themes */}
          {result.themes?.length > 0 && (
            <div className="rsa-section">
              <h3 className="rsa-section__title">Analyse par thématique</h3>
              <div className="rsa-theme-list">
                {result.themes.map((t) => (
                  <div key={t.id} className="rsa-theme-row">
                    <div className="rsa-theme-row__head">
                      <SentimentDot sentiment={t.sentiment} />
                      <span className="rsa-theme-row__name">{t.name}</span>
                      <span className="rsa-theme-row__rate">
                        {safeText(t.mentionRate)}
                      </span>
                      <span className="rsa-theme-row__score">
                        {t.score ?? "—"}/100
                      </span>
                    </div>
                    <ScoreBar score={t.score} />
                    {t.insight && (
                      <p className="rsa-theme-row__insight">
                        {safeText(t.insight)}
                      </p>
                    )}
                    {t.verbatims?.length > 0 && (
                      <div className="rsa-theme-row__verbatims">
                        {t.verbatims.map((v, i) => {
                          const text = safeText(v);
                          return (
                            <span
                              key={i}
                              className={`rsa-verbatim ${text.startsWith("+") ? "rsa-verbatim--pos" : "rsa-verbatim--neg"}`}
                            >
                              {text}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Detected themes */}
          {result.detectedThemes?.length > 0 && (
            <div className="rsa-section">
              <h3 className="rsa-section__title">
                Thématiques détectées par l'IA
              </h3>
              <div className="rsa-detected-list">
                {result.detectedThemes.map((t, i) => (
                  <div key={i} className="rsa-detected-chip">
                    <SentimentDot sentiment={t.sentiment} />
                    <span>{safeText(t.name)}</span>
                    <span className="rsa-detected-chip__score">
                      {t.score ?? "—"}/100
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SWOT */}
          {result.swot && (
            <div className="rsa-section">
              <h3 className="rsa-section__title">Analyse SWOT</h3>
              <div className="rsa-swot">
                <div className="rsa-swot__card rsa-swot__card--s">
                  <div className="rsa-swot__card-header">Forces</div>
                  <ul>
                    {(result.swot.strengths || []).map((s, i) => (
                      <li key={i}>{safeText(s)}</li>
                    ))}
                  </ul>
                </div>
                <div className="rsa-swot__card rsa-swot__card--w">
                  <div className="rsa-swot__card-header">Faiblesses</div>
                  <ul>
                    {(result.swot.weaknesses || []).map((s, i) => (
                      <li key={i}>{safeText(s)}</li>
                    ))}
                  </ul>
                </div>
                <div className="rsa-swot__card rsa-swot__card--o">
                  <div className="rsa-swot__card-header">Opportunités</div>
                  <ul>
                    {(result.swot.opportunities || []).map((s, i) => (
                      <li key={i}>{safeText(s)}</li>
                    ))}
                  </ul>
                </div>
                <div className="rsa-swot__card rsa-swot__card--t">
                  <div className="rsa-swot__card-header">Menaces</div>
                  <ul>
                    {(result.swot.threats || []).map((s, i) => (
                      <li key={i}>{safeText(s)}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Recommendations */}
          {result.recommendations?.length > 0 && (
            <div className="rsa-section">
              <h3 className="rsa-section__title">Recommandations</h3>
              <div className="rsa-reco-list">
                {result.recommendations.map((r, i) => (
                  <div key={i} className="rsa-reco">
                    <span className="rsa-reco__num">{i + 1}</span>
                    <p>{safeText(r)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Demo CTA */}
          <div className="rsa-demo">
            <span className="rsa-demo__badge">Localranker</span>
            <p className="rsa-demo__title">
              Analysez tous vos avis automatiquement
            </p>
            <p className="rsa-demo__desc">
              Analysez en continu le sentiment de vos avis Google sur
              l'ensemble de vos établissements et pilotez votre e-réputation.
            </p>
            <a
              className="rsa-demo__cta"
              href="https://localranker.fr/demander-demo?utm_source=localranker.tech&utm_medium=reviews-semantic-analysis"
              target="_blank"
              rel="noopener noreferrer"
            >
              Demander une démo
            </a>
          </div>
        </div>
      )}

      {/* SEO */}
      <section className="rsa-seo">
        <div className="rsa-seo__inner">
          <article className="rsa-seo__block">
            <h2>Pourquoi analyser le sentiment de vos avis clients ?</h2>
            <p>
              Les avis clients sont une mine d'informations. Au-delà de la note
              moyenne, le contenu textuel de chaque avis révèle ce que vos
              clients apprécient, ce qui les déçoit, et les thématiques qui
              reviennent le plus souvent. L'analyse sémantique transforme ces
              retours en insights actionnables.
            </p>
          </article>

          <article className="rsa-seo__block">
            <h2>Comment fonctionne l'analyse ?</h2>
            <p>
              Notre outil utilise l'intelligence artificielle pour analyser vos
              avis en profondeur.
            </p>
            <div className="rsa-seo__steps">
              <div className="rsa-seo__step">
                <span className="rsa-seo__step-num">1</span>
                <div>
                  <strong>Importez vos avis</strong>
                  <p>
                    Collez vos avis ou importez un fichier CSV. Jusqu'à 500 avis
                    par analyse.
                  </p>
                </div>
              </div>
              <div className="rsa-seo__step">
                <span className="rsa-seo__step-num">2</span>
                <div>
                  <strong>Choisissez vos thématiques</strong>
                  <p>
                    Sélectionnez jusqu'à 5 thématiques parmi 24 critères :
                    accueil, qualité, prix, délais...
                  </p>
                </div>
              </div>
              <div className="rsa-seo__step">
                <span className="rsa-seo__step-num">3</span>
                <div>
                  <strong>Recevez votre rapport</strong>
                  <p>
                    Score par thématique, analyse SWOT, verbatims clés et
                    recommandations en quelques secondes.
                  </p>
                </div>
              </div>
            </div>
          </article>

          <article className="rsa-seo__block">
            <h2>
              Comment l'analyse sémantique améliore votre e-réputation ?
            </h2>
            <p>
              En identifiant précisément les points de friction et les points
              forts perçus par vos clients, vous pouvez prioriser vos actions
              d'amélioration, former vos équipes et mettre en avant vos atouts.
              L'analyse SWOT transforme vos avis en plan d'action concret.
            </p>
          </article>
        </div>
      </section>

      <Modal
        title="Encore une petite étape"
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
      >
        <FormMoreInfo
          onSubmit={doSubmit}
          submitLabel="Lancer l'analyse"
        />
      </Modal>

      <PageFooter />
    </div>
  );
};

export default ReviewSemanticAnalysis;
